import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { SettingForm, SettingProblem, SettingSubsection, TextField } from "../../components/admin/SettingPanel";
import panel from "../../components/admin/AdminPanel.module.css";
import { useToast } from "../../components/toast/toastContext";
import { fetchTrustedProxies, updateTrustedProxies } from "../../api/trustedProxies";

const SettingsWritePermission = "settings.write";

/** The most entries the server accepts. Mirrors `TrustedForwarding.MaximumEntries` on the server. */
const MaximumTrustedProxyEntries = 50;

const ipv4Pattern = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})(\/(\d{1,2}))?$/;
const ipv6Pattern = /^[0-9a-fA-F:.]+(\/\d{1,3})?$/;

/**
 * Whether one entry looks like an IP address or CIDR network. The server makes the authoritative check; this stops an obvious mistake from
 * being sent.
 */
function isTrustedProxyEntry(entry: string): boolean {
  const ipv4 = ipv4Pattern.exec(entry);
  if (ipv4) {
    const octets = [ipv4[1], ipv4[2], ipv4[3], ipv4[4]].map(Number);
    const prefix = ipv4[6] === undefined ? 32 : Number(ipv4[6]);
    return octets.every((octet) => octet <= 255) && prefix <= 32;
  }

  return entry.includes(":") && ipv6Pattern.test(entry);
}

/** The entries of a comma-separated list, with blank entries dropped. */
function entriesOf(text: string): string[] {
  return text.split(",").map((entry) => entry.trim()).filter((entry) => entry.length > 0);
}

/**
 * The trusted proxies an administrator edits: the load balancer's network, as IP addresses or CIDR networks, comma-separated. A saved list
 * takes effect on this node at once and on the others within a minute. The field refuses an entry that is not an address or network before
 * it is sent, and the server refuses it again.
 */
export function TrustedProxiesSection(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded" } | { kind: "error"; message: string }>({ kind: "loading" });
  const [draft, setDraft] = useState("");
  const [saved, setSaved] = useState(false);
  const [pending, setPending] = useState(false);

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const result = await fetchTrustedProxies();
    if (result.kind === "ok") {
      setDraft(result.response.addresses);
      setSaved(result.response.saved);
      setState({ kind: "loaded" });
    } else if (result.kind === "denied") {
      setState({ kind: "error", message: t("admin.trustedProxies.denied") });
    } else {
      setState({ kind: "error", message: result.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const entries = entriesOf(draft);
  const valid = entries.length > 0 && entries.length <= MaximumTrustedProxyEntries && entries.every(isTrustedProxyEntry);
  const canSave = valid && !pending;

  const save = async () => {
    if (!canSave) {
      return;
    }

    setPending(true);
    const result = await updateTrustedProxies(entries.join(", "));
    setPending(false);
    if (result.kind === "ok") {
      setDraft(result.response.addresses);
      setSaved(true);
      toast.show(t("admin.trustedProxies.saved"), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <SettingSubsection id="trusted-proxies" title={t("admin.trustedProxies.title")} explanation={t("admin.trustedProxies.explanation", { maximum: MaximumTrustedProxyEntries })}>
      {state.kind === "loading" && <LoadingState label={t("admin.trustedProxies.loading")} />}
      {state.kind === "error" && <ErrorState description={state.message} onRetry={() => void load()} />}
      {state.kind === "loaded" && (
        <>
          <SettingForm>
            <TextField
              id="trusted-proxy-addresses"
              label={t("admin.trustedProxies.addresses")}
              placeholder={t("admin.trustedProxies.placeholder")}
              value={draft}
              invalid={!valid}
              describedBy="trusted-proxies-validation"
              onChange={setDraft}
            />
            <button type="button" className="button button-secondary" disabled={!canSave} onClick={() => void save()}>
              {t("admin.trustedProxies.save")}
            </button>
          </SettingForm>
          {!valid ? (
            <SettingProblem id="trusted-proxies-validation">
              {entries.length === 0
                ? t("admin.trustedProxies.empty")
                : entries.length > MaximumTrustedProxyEntries
                  ? t("admin.trustedProxies.tooMany", { maximum: MaximumTrustedProxyEntries })
                  : t("admin.trustedProxies.badEntry")}
            </SettingProblem>
          ) : null}
          {!saved && <p className={panel.footnotes}>{t("admin.trustedProxies.notSaved")}</p>}
        </>
      )}
    </SettingSubsection>
  );
}
